import React from 'react';
import ReactDOM from 'react-dom/client';
import { installGlobalErrorReporter } from '@pc/core';
import { GlobalErrorBoundary, installChunkReloadHandler } from '@pc/ui';
import { initI18n } from '@pc/i18n';
import { App } from './App';
import { env } from './env';
import './index.css';

// Backstop for the stale-chunk failure mode: any `import('./foo')` that
// 404s because the user is on the old shell after a deploy will trigger
// a debounced hard reload instead of bubbling "Unexpected Application
// Error! Failed to fetch dynamically imported module" to the user.
// Code-split routes in App.tsx already use `lazyWithReload`; this
// listener catches every OTHER dynamic import (i18n bundles, third-party
// libs, ad-hoc `await import()` inside event handlers).
installChunkReloadHandler();

// i18n bootstrap. The auth site shares the same UI catalogs (@pc/i18n's
// common namespace) as web/admin/sell/shop so the language picker on
// the sign-in footer matches what the user gets inside the app.
initI18n({ app: 'auth' });

// Install the global error reporter BEFORE initSupabase so a failure
// during DB init still lands in the errors pipeline. Wires up:
//   * window.onerror + window.onunhandledrejection
//   * console.error breadcrumb wrapper
//   * fetch wrapper that adds x-request-id and records non-2xx as
//     breadcrumbs
//   * route / click breadcrumbs
installGlobalErrorReporter({
  app: 'auth',
  supabaseUrl: env.supabaseUrl,
  anonKey: env.supabaseAnonKey,
  release: (import.meta.env.VITE_BUILD_SHA as string | undefined) ?? null,
  environment: (import.meta.env.MODE as string | undefined) ?? 'production',
  // The auth site is pre-auth by definition; no active org/store
  // context exists. The reporter accepts a no-op resolver.
  getActiveContext: () => null,
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found.');

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <GlobalErrorBoundary>
      <App />
    </GlobalErrorBoundary>
  </React.StrictMode>,
);
